import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { string, ref } from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
} from "@mui/material";
import { useMutation } from "@apollo/client";

//Import from project
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { CREATE_SUBJECT, UPDATE_SUBJECT } from "../../Schema/Subject";

export default function SubjectForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createSubject] = useMutation(CREATE_SUBJECT, {
    onCompleted: ({ createSubject }) => {
      console.log("createSubject :", createSubject);
      setLoading(false);
      if (createSubject?.isSuccess === true) {
        setAlert(true, "success", createSubject?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createSubject?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  // console.log("createSubject :", createSubject({}));

  const [updateSubject] = useMutation(UPDATE_SUBJECT, {
    onCompleted: ({ updateSubject }) => {
      setLoading(false);
      if (updateSubject?.isSuccess === true) {
        setAlert(true, "success", updateSubject?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateSubject?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    remark: Yup.string(),
    khName: Yup.string().required(t("required")),
    enName: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      remark: "",
      khName: "",
      enName: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createSubject({
          variables: {
            input: {
              ...values,
            },
          },
        });
      } else {
        updateSubject({
          variables: {
            id: editData?._id,
            input: {
              ...values,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Update" && editData) {
      setFieldValue("remark", editData?.remark);
      setFieldValue("khName", editData?.khName);
      setFieldValue("enName", editData?.enName);
    }

    if (dialogTitle === "Create" && open) {
      resetForm();
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_subject")
            : t("modal_title_update_subject")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_khmer_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_khmer_name")}
                  {...getFieldProps("khName")}
                  error={Boolean(touched.khName && errors.khName)}
                  helperText={touched.khName && errors.khName}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_english_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_english_name")}
                  {...getFieldProps("enName")}
                  error={Boolean(touched.enName && errors.enName)}
                  helperText={touched.enName && errors.enName}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  rows={2}
                  multiline
                  fullWidth
                  placeholder={t("thead_remark")}
                  {...getFieldProps("remark")}
                  error={Boolean(touched.remark && errors.remark)}
                  helperText={touched.remark && errors.remark}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
